import { Nullable } from "../../types/Nullable";
import { CustomSleepLogFilter, SimpleSleepLogFilterSet, SleepLogFilter } from "../../types/SleepLogFilter";
import SleepLog, { Medication, Treatment } from "../../types/SleepLog";
import SleepLogSettings, { CustomMetric } from "../../types/SleepLogSettings";
import { HttpService } from "./HttpService";
import { DateTime } from "luxon";
import { fetchWithRetry } from "../../utils/fetch-utils";
import { LocalHealthData } from "../../types/LocalHealthData";
import { SleepLogQuery, UpsertSleepLogQuery } from "../../types/SavedQuery";
import { SleepLogSummary, SleepLogSummaryListItem, SummaryConversationMessage, SummaryPeriodType } from "../../types/SleepLogSummary";

export type SummaryListQuery =
    | { minDate: string; maxDate: string }
    | { ids: string[] }
    | { periodType?: SummaryPeriodType; limit: number; scanForward?: boolean; exclusiveStartId?: string };

export type AIAgentStep =
    | {
          type: "function_call";
          name: string;
          arguments: Record<string, unknown>;
          call_id: string;
      }
    | {
          type: "function_call_output";
          call_id: string;
          output: string;
      }
    | {
          type: "reasoning";
          text: string;
      }
    | {
          type: "message";
          text: string;
      };

export interface AIAgentResponse {
    responseId: string;
    steps: AIAgentStep[];
}

export default class SleepLogHttpService extends HttpService {

    public async getQueries() {
        const res = await fetchWithRetry(`${this.baseAccountPath}/queries`, {
            method: 'GET',
            headers: {
                ...this.commonHeaders,
            },
            credentials: 'include'
        });

        const body = await res.json();

        if (res.status !== 200) {
            HttpService.throwHttpError(res, body);
        }

        return body as SleepLogQuery[];
    }

    public async createQuery(query: UpsertSleepLogQuery) {
        const res = await fetchWithRetry(`${this.baseAccountPath}/queries`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                ...this.commonHeaders,
            },
            body: JSON.stringify(query),
            credentials: 'include'
        });

        const body = await res.json();

        if (res.status !== 201 && res.status !== 200) {
            HttpService.throwHttpError(res, body);
        }

        return body as SleepLogQuery;
    }

    public async updateQuery(id: string, query: UpsertSleepLogQuery) {
        const res = await fetchWithRetry(`${this.baseAccountPath}/queries/${id}`, {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
                ...this.commonHeaders,
            },
            body: JSON.stringify(query),
            credentials: 'include'
        });

        const body = await res.json();

        if (res.status !== 200) {
            HttpService.throwHttpError(res, body);
        }

        return body as SleepLogQuery;
    }

    public async deleteQuery(id: string) {
        const res = await fetchWithRetry(`${this.baseAccountPath}/queries/${id}`, {
            method: 'DELETE',
            headers: {
                ...this.commonHeaders,
            },
            credentials: 'include'
        });

        if (res.status === 204) {
            return;
        }

        const body = await res.json();

        if (res.status !== 200) {
            HttpService.throwHttpError(res, body);
        }
    }

    public async getSleepLogs(
        simpleFilter?: SimpleSleepLogFilterSet,
        advancedFilter?: CustomSleepLogFilter,
        limit?: number,
        scanForward?: boolean,
        exclusiveStartId?: string,
        useEventualConsistency?: boolean,
    ) {
        let query = `scan-forward=${scanForward}&limit=${limit}`;

        if (exclusiveStartId) {
            query += `&exclusive-start-id=${exclusiveStartId}`;
        }

        if (simpleFilter) {
            query += `&filter=${JSON.stringify(simpleFilter)}`;
        }
        else if (advancedFilter) {
            query += `&advancedFilter=${JSON.stringify(advancedFilter)}`;                
        }

        if (useEventualConsistency) {
            query += `&use-eventual-consistency=true`;
        }

    const res = await fetchWithRetry(`${this.baseAccountPath}/sleep-logs?${query}`, {
            headers: {
                ...this.commonHeaders,
            },
            credentials: 'include'
        });

        const body = await res.json();
        
        if (res.status !== 200) {
            HttpService.throwHttpError(res, body);                
        }
        
        return body as { logs: SleepLog[], lastEvaluatedId?: string };
    }

    public async createSleepLog(
        date: string,
        notes?: string,
        customValues?: CustomMetric[],
        primaryConnectedAccount?: string,
        tags?: string[],
        treatments?: Treatment[],
        medications?: Medication[],
        otherMedications?: Medication[]
    ) {
        const sleepLogToCreate: Record<string, any> = {
            date: date,
            notes: notes,
            customMetrics: customValues,
            primaryConnectedAccount: primaryConnectedAccount
        };

        if (tags && tags.length > 0) { sleepLogToCreate.tags = tags; }
        if (treatments && treatments.length > 0) { sleepLogToCreate.treatments = treatments; }
        if (medications && medications.length > 0) { sleepLogToCreate.medications = medications; }
        if (otherMedications && otherMedications.length > 0) { sleepLogToCreate.otherMedications = otherMedications; }

    const res = await fetchWithRetry(`${this.baseAccountPath}/sleep-logs`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                ...this.commonHeaders,               
            },
            body: JSON.stringify(sleepLogToCreate),
            credentials: 'include'
        });

        const body = await res.json();

        if (res.status !== 201) {
            HttpService.throwHttpError(res, body);    
        }

        return body as SleepLog;
    }

    public async deleteSleepLog(id: string) {
    const res = await fetchWithRetry(`${this.baseAccountPath}/sleep-log/${id}`, {
            method: 'DELETE',
            headers: {
                ...this.commonHeaders,
            },
            credentials: 'include'
        });

        if (res.status !== 200) {
            const body = await res.json();
            HttpService.throwHttpError(res, body);    
        }
    }

    public async updateSleepLog(
        id: string,
        update: Nullable<Omit<SleepLog, "id" | "date">>,
        lastModifiedTimeCondition?: string,
    ) {
        let headers: any = {
            'Content-Type': 'application/json',
            ...this.commonHeaders,
        };

        if (lastModifiedTimeCondition !== undefined) {
            headers["x-if-match"] = lastModifiedTimeCondition;
        }

    const res = await fetchWithRetry(`${this.baseAccountPath}/sleep-log/${id}`, {
            method: 'PATCH',
            headers: headers,
            body: JSON.stringify(update),
            credentials: 'include'
        });

        const body = await res.json();
        
        if (res.status !== 200) {
            HttpService.throwHttpError(res, body);    
        }

        return body as { id: string, lastModifiedTime: string };        
    }

    public async getSleepLogSettings() {
    const res = await fetchWithRetry(`${this.baseAccountPath}/settings/sleep-log-settings`, {
            method: 'GET',
            headers: {
                ...this.commonHeaders,
            },
            credentials: 'include'
        });
    
        const body = await res.json();

        if (res.status !== 200) {
            HttpService.throwHttpError(res, body);    
        }      

        return body as SleepLogSettings;
    }

    public async putSleepLogSettings(settings: SleepLogSettings) {
    const res = await fetchWithRetry(`${this.baseAccountPath}/settings/sleep-log-settings`, {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
                ...this.commonHeaders,
            },
            body: JSON.stringify(settings),
            credentials: 'include'            
        });

        if (res.status !== 201) {
            const body = await res.json();
            HttpService.throwHttpError(res, body);    
        }
    }

    public async patchSleepLogSettings(partialSettings: SleepLogSettings) {
    const res = await fetchWithRetry(`${this.baseAccountPath}/settings/sleep-log-settings`, {
            method: 'PATCH',
            headers: {
                'Content-Type': 'application/merge-patch+json',
                ...this.commonHeaders,
            },
            body: JSON.stringify(partialSettings),
            credentials: 'include'            
        });

        const body = await res.json();

        if (res.status !== 200) {
            HttpService.throwHttpError(res, body);    
        }

        return body as SleepLogSettings;
    }    

    public async getTagSuggestions(from: DateTime, to: DateTime) {
    const res = await fetchWithRetry(`${this.baseAccountPath}/sleep-logs/suggestions?from=${from.toISODate()}&to=${to.toISODate()}`, {
            method: 'GET',
            headers: {
                ...this.commonHeaders,
            },
            credentials: 'include'            
        });

        const body = await res.json();

        if (res.status !== 200) {
            HttpService.throwHttpError(res, body);    
        }      

        return body as { [key: string]: [string, number][] };        
    }

    public async syncSleepLog(id: string, localSleepData?: LocalHealthData) {
    const res = await fetchWithRetry(`${this.baseAccountPath}/connectedAccounts/sync/dest/sleep-logs/${id}`, {
            method: 'POST',
            headers: {
                ...this.commonHeaders,
                'Content-Type': 'application/json',
            },
            credentials: 'include',
            body: localSleepData ? JSON.stringify(localSleepData) : undefined
        });
        
        const body = await res.json();

        if (res.status !== 200) {
            HttpService.throwHttpError(res, body);    
        }
        
        return body as SleepLog;
    }

    /**
     * Geta a summary of sleep logs using within a date range
     * @param from YYYY-MM-DD
     * @param to YYYY-MM-DD
     * @param responseMode 
     * @param customFilter Optional custom filter to apply
     * @returns Summary of logs in markdown format
     */
    public async getAISummary(from: string, to: string, responseMode: 'fast' | 'thinking', customFilters?: SleepLogFilter[][]) {
        const filter = {
            minDate: from,
            maxDate: to,
            filters: customFilters
        };

        const url = `${this.baseAccountPath}/sleep-logs/ai-summary?filter=${encodeURIComponent(JSON.stringify(filter))}&responseMode=${responseMode}`;

    const res = await fetchWithRetry(url, {
            method: 'GET',
            headers: {
                ...this.commonHeaders,
            },
            credentials: 'include'            
        });

        const body = await res.json();

        if (res.status !== 200) {
            HttpService.throwHttpError(res, body);    
        }      

        return body as { summary: string };        
    }

    /**
     * Get a response from the AI model based on the provided messages and date range
     * @param from 
     * @param to 
     * @param messages 
     * @param responseMode
     * @param customFilter Optional custom filter to apply
     * @returns 
     */
    public async getAIResponse(
        from: string,
        to: string,
        messages: { role: "user" | "assistant", content: string }[],
        responseMode: 'fast' | 'thinking',
        customFilter?: SleepLogFilter[][]
    ) {
        const filter = {
            minDate: from,
            maxDate: to,
            filters: customFilter
        };

        const url = `${this.baseAccountPath}/sleep-logs/ai-conversation?filter=${encodeURIComponent(JSON.stringify(filter))}&responseMode=${responseMode}`;

    const res = await fetchWithRetry(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                ...this.commonHeaders,
            },
            body: JSON.stringify({ messages }),
            credentials: 'include'            
        });

        const body = await res.json();

        if (res.status !== 200) {
            HttpService.throwHttpError(res, body);    
        }      

        return body as { responseMessage: string };        
    }    

    /**
     * Send a single prompt to the agent endpoint and return the structured steps.
     * @param input
     * @param previousResponseId
     * @param responseMode
     */
    public async getAIAgentResponse(
        input: string,
        previousResponseId?: string,
        responseMode: 'fast' | 'thinking' = 'fast'
    ) {
        const url = `${this.baseAccountPath}/sleep-logs/agent`;

        const res = await fetchWithRetry(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                ...this.commonHeaders,
            },
            body: JSON.stringify({ input, previousResponseId, responseMode }),
            credentials: 'include'
        });

        const body = await res.json();

        if (res.status !== 200) {
            HttpService.throwHttpError(res, body);
        }

        return body as AIAgentResponse;
    }

    // --- Period summaries ---

    private summaryPath(id?: string) {
        const base = `${this.baseAccountPath}/sleep-log-summaries`;
        return id ? `${base}/${encodeURIComponent(id)}` : base;
    }

    private static async readBody(res: Response) {
        const text = await res.text();
        return text ? JSON.parse(text) : {};
    }

    public async getSummaries(query: SummaryListQuery) {
        const params = new URLSearchParams();

        if ("ids" in query) {
            params.set("ids", query.ids.join(","));
        }
        else if ("minDate" in query) {
            params.set("minDate", query.minDate);
            params.set("maxDate", query.maxDate);
        }
        else {
            if (query.periodType) params.set("periodType", query.periodType);
            params.set("limit", String(query.limit));
            params.set("scanForward", String(query.scanForward ?? false));
            if (query.exclusiveStartId) params.set("exclusiveStartId", query.exclusiveStartId);
        }

        const res = await fetchWithRetry(`${this.summaryPath()}?${params.toString()}`, {
            method: 'GET',
            headers: { ...this.commonHeaders },
            credentials: 'include'
        });

        const body = await SleepLogHttpService.readBody(res);

        if (res.status !== 200) {
            HttpService.throwHttpError(res, body);
        }

        return body as { summaries: SleepLogSummaryListItem[]; lastEvaluatedId?: string };
    }

    public async ensureSummaries(today: string, periods: { type: SummaryPeriodType; startDate: string }[]) {
        const res = await fetchWithRetry(`${this.summaryPath()}/ensure`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                ...this.commonHeaders,
            },
            body: JSON.stringify({ today, periods }),
            credentials: 'include'
        });

        const body = await SleepLogHttpService.readBody(res);

        if (res.status !== 200) {
            HttpService.throwHttpError(res, body);
        }

        return body as { summaries: SleepLogSummaryListItem[] };
    }

    public async getSummary(id: string) {
        const res = await fetchWithRetry(this.summaryPath(id), {
            method: 'GET',
            headers: { ...this.commonHeaders },
            credentials: 'include'
        });

        const body = await SleepLogHttpService.readBody(res);

        if (res.status !== 200) {
            HttpService.throwHttpError(res, body);
        }

        return body as SleepLogSummary;
    }

    public async generateSummary(id: string) {
        return this.postSummaryAction(id, "generate");
    }

    public async recalculateSummary(id: string) {
        return this.postSummaryAction(id, "recalculate");
    }

    private async postSummaryAction(id: string, action: "generate" | "recalculate") {
        const res = await fetchWithRetry(`${this.summaryPath(id)}/${action}`, {
            method: 'POST',
            headers: { ...this.commonHeaders },
            credentials: 'include'
        });

        const body = await SleepLogHttpService.readBody(res);

        if (res.status !== 202) {
            HttpService.throwHttpError(res, body);
        }

        return (body as { summary: SleepLogSummary }).summary;
    }

    public async sendSummaryMessage(id: string, messages: SummaryConversationMessage[], responseMode: 'fast' | 'thinking') {
        const res = await fetchWithRetry(`${this.summaryPath(id)}/conversation`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                ...this.commonHeaders,
            },
            body: JSON.stringify({ messages: messages.slice(-20), responseMode }),
            credentials: 'include'
        });

        const body = await SleepLogHttpService.readBody(res);

        if (res.status !== 200) {
            HttpService.throwHttpError(res, body);
        }

        return body as { responseMessage: string };
    }
}
